import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Edit, Trash2, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';

const PatientCard = ({ 
  patient, 
  groupes,
  customFields,
  onEdit, 
  onDelete, 
  onViewNotes,
  patientLabel,
  patientArticle
}) => {
  const getGroupeNom = (groupeId) => {
    const groupe = groupes.find(g => g.id === groupeId);
    return groupe ? groupe.nom : 'Aucun groupe';
  };

  const getGroupeCouleur = (groupeId) => {
    const groupe = groupes.find(g => g.id === groupeId);
    return groupe ? groupe.couleur : '#6B7280';
  };

  const renderFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return <span className="text-gray-400 italic">Non renseigné</span>;
    }
    
    switch (field.field_type) {
      case 'checkbox':
        return value ? 'Oui' : 'Non';
      case 'select':
        return value;
      case 'date':
        return new Date(value).toLocaleDateString('fr-FR');
      default:
        return value;
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg">
                {patient.prenom} {patient.nom}
              </h3>
              {patient.groupe_id && (
                <Badge 
                  variant="secondary"
                  style={{ 
                    backgroundColor: getGroupeCouleur(patient.groupe_id) + '20',
                    color: getGroupeCouleur(patient.groupe_id),
                    borderColor: getGroupeCouleur(patient.groupe_id)
                  }}
                >
                  {getGroupeNom(patient.groupe_id)}
                </Badge>
              )}
            </div>
            
            {/* Dynamic custom fields */}
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-2">
              {customFields
                .filter(f => f.is_active)
                .slice(0, 4)
                .map(field => (
                  <div key={field.id} className="flex items-center gap-1">
                    <span className="text-gray-400">{field.name}:</span>
                    <span>{renderFieldValue(field, patient.custom_fields?.[field.field_key])}</span>
                  </div>
                ))}
            </div>
            
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <BookOpen className="h-4 w-4" />
                {patient.notes_count || 0} note(s)
              </span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onViewNotes(patient)}
              data-testid={`view-notes-${patient.id}`}
            >
              <BookOpen className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onEdit(patient)}
              data-testid={`edit-patient-${patient.id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-red-500 hover:text-red-700"
              onClick={() => onDelete(patient)}
              data-testid={`delete-patient-${patient.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PatientList = ({ 
  patients, 
  groupes,
  customFields,
  currentPage,
  itemsPerPage,
  onPageChange,
  onEdit,
  onDelete,
  onViewNotes,
  patientLabel,
  patientLabelPlural,
  patientArticle
}) => {
  const totalPages = Math.ceil(patients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPatients = patients.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Liste des {patientLabelPlural} ({patients.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {paginatedPatients.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            Aucun {patientLabel} trouvé. Créez votre premier {patientLabel} pour commencer.
          </p>
        ) : (
          <>
            <div className="space-y-4">
              {paginatedPatients.map(patient => (
                <PatientCard
                  key={patient.id}
                  patient={patient}
                  groupes={groupes}
                  customFields={customFields}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onViewNotes={onViewNotes}
                  patientLabel={patientLabel}
                  patientArticle={patientArticle}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, patients.length)} sur {patients.length} {patientLabelPlural}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="flex items-center px-3 text-sm">
                    Page {currentPage} sur {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PatientList;
